import { Button } from "@/components/ui/button";
import { Sparkles, Droplets, Zap, Heart } from "lucide-react";
import glowMachine from "@/assets/glowsolution-geraet-beauty-nuernberg.jpg";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const PLANITY_URL = "https://www.planity.com/de-DE/22-studios-90469-nurnberg";

interface GlowSolutionHighlightProps {
  variant?: "compact" | "full";
}

const features = [
  {
    icon: Droplets,
    title: "GlowPurify",
    description: "Porentiefe Reinigung für ein klares Hautbild",
  },
  {
    icon: Sparkles,
    title: "GlowPolish",
    description: "Intensives Peeling für samtweiche Haut",
  },
  {
    icon: Zap,
    title: "GlowPermeation",
    description: "Tiefenwirksame Wirkstoffzufuhr per Ultraschall",
  },
  {
    icon: Heart,
    title: "GlowPush-up",
    description: "Liftende SMAS-Massage für Straffung",
  },
];

const GlowSolutionHighlight = ({ variant = "compact" }: GlowSolutionHighlightProps) => {
  if (variant === "compact") {
    return (
      <TooltipProvider delayDuration={100}>
        <div className="relative bg-gradient-to-br from-background via-secondary/30 to-background rounded-3xl p-8 md:p-10 border border-border/40 overflow-hidden group">
          {/* Subtle glow effect */}
          <div className="absolute -top-20 -right-20 w-60 h-60 bg-primary/10 rounded-full blur-3xl opacity-50 group-hover:opacity-80 transition-opacity duration-700" />
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-highlight/10 rounded-full blur-2xl opacity-30 group-hover:opacity-60 transition-opacity duration-700" />
          
          <div className="relative z-10 flex flex-col lg:flex-row gap-8 items-center">
            {/* Image with hover effect */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-full lg:w-1/3 cursor-pointer">
                  <div className="relative rounded-2xl overflow-hidden shadow-soft-lg hover-pop group/image">
                    <img
                      src={glowMachine}
                      alt="GlowSolution Gerät"
                      className="w-full h-64 lg:h-80 object-cover transition-transform duration-500 group-hover/image:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover/image:opacity-100 transition-opacity duration-300 flex items-end p-4">
                      <span className="text-sm font-medium text-white">Made in Germany</span>
                    </div>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-background border-border/50 shadow-lg p-3 max-w-xs">
                <p className="text-sm">High-End Hydrafacial-Technologie für sofort sichtbare Ergebnisse – Made in Germany</p>
              </TooltipContent>
            </Tooltip>

            {/* Content */}
            <div className="flex-1 space-y-6 text-center lg:text-left">
              <div className="space-y-2">
                <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary/20 text-foreground border border-primary/30">
                  ✨ Highlight Technologie
                </span>
                <h3 className="text-2xl md:text-3xl font-serif font-light text-foreground">
                  GlowSolution
                </h3>
                <p className="text-muted-foreground text-sm md:text-base">
                  4-in-1 Hydrafacial für sofort prallere, strahlendere Haut – ohne Ausfallzeit.
                </p>
              </div>

              {/* Feature Pills with Tooltips */}
              <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                {features.map((feature) => (
                  <Tooltip key={feature.title}>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-background/80 border border-border/50 cursor-pointer hover:bg-primary/10 hover:border-primary/30 transition-all duration-300 hover:scale-105">
                        <feature.icon className="w-4 h-4 text-primary" />
                        <span className="text-xs font-medium">{feature.title}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-background border-border/50 shadow-lg p-3 max-w-[200px]">
                      <p className="text-sm font-medium mb-1">{feature.title}</p>
                      <p className="text-xs text-muted-foreground">{feature.description}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>

              <Button
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-blush-dark px-8 py-5 text-sm font-medium rounded-full hover-lift shadow-glow"
                onClick={() => window.open(PLANITY_URL, "_blank")}
              >
                Jetzt Glow-Behandlung buchen
              </Button>
            </div>
          </div>
        </div>
      </TooltipProvider>
    );
  }

  // Full variant for facials page
  return (
    <TooltipProvider delayDuration={100}>
      <div className="relative bg-gradient-to-br from-secondary/50 via-background to-secondary/30 rounded-2xl md:rounded-3xl p-5 md:p-12 border border-border/40 overflow-hidden">
        {/* Animated glow effects */}
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-primary/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-highlight/10 rounded-full blur-2xl" />
        
        <div className="relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Image */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="order-2 lg:order-1 cursor-pointer">
                  <div className="relative rounded-2xl overflow-hidden shadow-soft-lg hover-pop group">
                    <img
                      src={glowMachine}
                      alt="GlowSolution High-Tech Gerät"
                      className="w-full h-80 lg:h-[450px] object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="text-sm font-medium text-white">High-End Profi-Technologie</p>
                      <p className="text-xs text-white/80">Made in Germany • Patentierte Stream-Ultraschall-Technik</p>
                    </div>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-background border-border/50 shadow-xl p-4 max-w-xs">
                <p className="text-sm font-medium mb-2">GlowSolution Technologie</p>
                <p className="text-xs text-muted-foreground">Professionelles 4-in-1 Hydroderm-Gerät mit patentierter Stream-Ultraschall-Technik für beste Wirkstoffaufnahme.</p>
              </TooltipContent>
            </Tooltip>

            {/* Content */}
            <div className="order-1 lg:order-2 space-y-8">
              <div className="space-y-4">
                <span className="inline-block px-4 py-2 text-xs font-medium rounded-full bg-primary/20 text-foreground border border-primary/30">
                  ✨ High-Tech Hydrafacial
                </span>
                <h3 className="text-3xl md:text-4xl font-serif font-light text-foreground">
                  GlowSolution
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Erlebe die ultimative Gesichtsbehandlung: 4 aufeinander abgestimmte Schritte für sofort prallere, glattere und strahlendere Haut – ganz ohne Ausfallzeit.
                </p>
              </div>

              {/* Features Grid with Tooltips */}
              <div className="grid grid-cols-2 gap-2 md:gap-4">
                {features.map((feature) => (
                  <Tooltip key={feature.title}>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-2 md:gap-3 p-2.5 md:p-4 rounded-xl bg-background/60 border border-border/40 cursor-pointer hover:bg-primary/10 hover:border-primary/30 hover:shadow-md transition-all duration-300 hover:scale-[1.02]">
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                          <feature.icon className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                        </div>
                        <span className="text-xs md:text-sm font-medium">{feature.title}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-background border-border/50 shadow-xl p-4 max-w-[220px]">
                      <p className="text-sm font-medium mb-1">{feature.title}</p>
                      <p className="text-xs text-muted-foreground">{feature.description}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>

              {/* Benefits */}
              <div className="flex flex-wrap gap-2">
                {["Sofort sichtbare Ergebnisse", "Keine Ausfallzeit", "Alle Hauttypen"].map((benefit) => (
                  <span key={benefit} className="px-3 py-1 text-xs rounded-full bg-secondary/80 text-muted-foreground border border-border/30">
                    ✓ {benefit}
                  </span>
                ))}
              </div>

              <Button
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-blush-dark px-10 py-6 text-base font-medium rounded-full hover-lift shadow-glow w-full sm:w-auto"
                onClick={() => window.open(PLANITY_URL, "_blank")}
              >
                Jetzt Glow-Behandlung buchen
              </Button>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default GlowSolutionHighlight;
